import { ATRAX_ADDRESS_TYPE, USER_ADDRESS_TYPE } from "@/constants/message";
import { User } from "@/types/users";

import { MessageType } from "@/types/message";
import { isEncryptedMessage } from "@/utils/crypto";

/**
 * Sanitizes file names that are uploaded to weblinq by removing any non-alphanumeric
 * (& '.' or '-') to an underscore.
 *
 * @param {string} input - The string to sanitize.
 * @returns {String} The sanitized string.
 */
export function sanitizeInput(input: string) {
  return String(input).replace(/[^a-zA-Z0-9.-]/g, "_");
}

/**
 * Determines if the provided string represents a photo message.
 *
 * @param {string} contents - A string representing the message contents.
 * @returns `true` if the message is a photo message (i.e., has a `cls` property with the value "mms").
 *          Returns `false` if the string cannot be parsed as JSON.
 */
export function isPhotoMessage(contents: string) {
  try {
    const j = JSON.parse(contents);
    if (j.cls == "mms") {
      return true;
    }
  } catch (_) {
    return false;
  }
}

/**
 * Checks if the provided string is valid JSON.
 *
 * @param {string} str - A string to be checked.
 * @returns `true` if the string can be parsed as JSON, otherwise `false`.
 */
export function isValidJSON(str: string) {
  try {
    JSON.parse(str);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Checks if the provided string is a JSON object or array — a machine payload (media,
 * key exchange) rather than something a user typed.
 *
 * Narrower than {@link isValidJSON} on purpose: bare scalars parse too, so `"42"`, `"true"`
 * and `"null"` would be mistaken for payloads and the messages holding them dropped.
 *
 * @param {string} str - A string to be checked.
 * @returns `true` only if the string parses to a JSON object or array.
 */
export function isJsonPayload(str: string) {
  // Prefix test first: plain text never reaches JSON.parse, which matters for the multi-MB
  // base64 bodies of attachments.
  const firstChar = str.trimStart()[0];
  if (firstChar !== "{" && firstChar !== "[") return false;

  try {
    JSON.parse(str);
    return true;
  } catch (_) {
    return false;
  }
}

/** Escapes regex metacharacters so user input can be used inside a RegExp literally. */
function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function parseAddress(address: string, addressType: number) {
  if (addressType === USER_ADDRESS_TYPE || addressType === ATRAX_ADDRESS_TYPE) {
    return address;
  }

  return JSON.parse(address);
}

/**
 * Extracts a username and a first/last name from a fullname.
 *
 * If `groupName` is provided, it replaces `fullname` and uses (Group) as the name.
 * Otherwise, extracts the portion before parens as the username and the rest as the user's name.
 *
 * @param {string} fullname - The user's full name in the form `username (firstName lastName)`.
 * @param {string} groupName - The user's group name, if any.
 * @returns {Array<string>} An array with the username and the user's first + last name.
 */

export function processFullname(
  fullname: string,
  groupName: string
): Array<string> {
  let username = fullname;
  let name = "";

  if (groupName) {
    username = groupName;
    name = " (Group)";
  } else {
    const parensIndex = fullname.indexOf("(");

    // user has a first and/or last name set, override default
    if (parensIndex >= 0) {
      username = fullname.slice(0, parensIndex);
      name = ` ${fullname.slice(parensIndex)}`;
    }
  }

  return [username, name];
}

/**
 * Generates a list of suggestions based on user input, filtering users and groups.
 *
 * @param {string} input - The input string used for filtering.
 * @param {User[]} users - The arrayy of users to filter.
 * @param {Group[]} groups - The array of groups to filter.
 * @returns {string[]} An array of formatted suggestions based on the input.
 */
export const generateSuggestions = (
  input: string,
  users: User[],
  //groups: Group[]
): string[] => {
  if (!input) return [];
  const loweredInput = input.toLowerCase();

  const userSuggestions = users
    .filter((user) =>
      `${user.username} ${user.firstname} ${user.lastname}`
        .toLowerCase()
        .includes(loweredInput)
    )
    .map((user) => {
      if (!user.firstname || !user.lastname) {
        return `${user.username}`;
      } else {
        return `${user.username} (${user.firstname} ${user.lastname})`;
      }
    });

  // Groups are intentionally excluded — users cannot start new group conversations.
  // const groupSuggestions = groups
  //   .filter(
  //     (group) =>
  //       group.name.toLowerCase().includes(loweredInput) ||
  //       "group".includes(loweredInput)
  //   )
  //   .map((group) => `${group.name} (Group)`);

  return [...userSuggestions];
};

/**
 * Filters messages based on a keyword, ignoring media messages (which should be in JSON format).
 *
 * @param {string} keyword - The keyword to search for within the messages.
 * @param {boolean} isGroup - Whether the message is from a group chat or single user.
 * @param {MessageType[]} messages - The array of messages to filter.
 * @returns {MessageType[]} - The filtered list of messages containing the keyword.
 */
export const filterMessages = (
  keyword: string,
  isGroup: boolean,
  messages: MessageType[]
) => {
  return messages.filter((msg) => {
    // Skip machine payloads (media, key exchange). Not isValidJSON: that also matched bare
    // scalars, so a message of just "42" was dropped before the keyword ever ran.
    if (isJsonPayload(msg.message)) {
      return false;
    }
    // Nothing readable to match: a decrypt-failure placeholder (by its field, never its text) or
    // ciphertext that was never decrypted.
    if (msg.decryptStatus || isEncryptedMessage(msg.message)) {
      return false;
    }

    // Don't include usernames in the search for the keyword
    if (isGroup) {
      const colonIndex = msg.message.indexOf(":");

      if (colonIndex !== -1) {
        return msg.message
          .slice(colonIndex + 1)
          .toLowerCase()
          .includes(keyword.toLowerCase());
      }
    }

    return msg.message.toLowerCase().includes(keyword.toLowerCase());
  });
};

/**
 * Formats a user's full name based on available information.
 *
 * @param {string} username - The username of the user.
 * @param {string} [firstName] - The user's first name (optional).
 * @param {string} [lastName] - The user's last name (optional).
 * @returns {string} - The formatted full name in the format: "username (First Last)" if available,
 *                     otherwise "username (First)" or "username (Last)", or just "username".
 */
export const formatFullname = (
  username: string,
  firstName?: string,
  lastName?: string
) => {
  if (firstName && lastName) return `${username} (${firstName} ${lastName})`;
  if (firstName) return `${username} (${firstName})`;
  if (lastName) return `${username} (${lastName})`;
  return username;
};

/**
 * Extracts the substring before the first colon in the message if it's a group message.
 * If it's not a group or there isn't a colon, it returns the fallback name.
 *
 * @param {boolean} isGroup - A boolean flag indicating if the message is part of a group.
 * @param {string} message - The string representing the message.
 * @param {string} name - The name to return if it's not a group message or there's no colon.
 * @returns The member's name or the substring before the first colon in the message if it's a group.
 */
export const displayMemberName = (
  isGroup: boolean,
  message: string,
  fallbackName: string
) => {
  if (!isGroup) return fallbackName;

  const colonIndex = message.indexOf(":");

  return colonIndex !== -1
    ? message.slice(0, colonIndex)
    : fallbackName.split("(")[0] + "(Group)";
};

/**
 * Truncates a message around a keyword, ensuring it doesn't cut off words and highlights the keyword.
 *
 * @param {boolean} isGroup - Whether the message is from a group chat or a single user.
 * @param {string} keyword - The keyword or phrase to highlight.
 * @param {string} message - The full message containing the keyword.
 * @param {number} contextLength - The total length of surrounding context to preserve.
 * @returns - The start + end indexes of the truncated message + the message in array form, split by the keyword
 */
export const highlightKeyword = (
  isGroup: boolean,
  keyword: string,
  message: string,
  contextLength: number = 30
): [number, number, string[]] => {
  // Don't include username from group message in the search results
  const colonIndex = isGroup ? message.indexOf(":") : -1;
  const messageToSearch =
    colonIndex !== -1 ? message.slice(colonIndex + 1) : message;
  const keywordIndex = messageToSearch
    .toLowerCase()
    .indexOf(keyword.toLowerCase());

  let start = Math.max(0, keywordIndex - Math.floor(contextLength / 2));
  let end = Math.min(
    messageToSearch.length,
    keywordIndex + keyword.length + Math.floor(contextLength / 2)
  );

  // Try not to  cut off words
  if (start > 0 && messageToSearch[start - 1] !== " ") {
    while (start > 0 && messageToSearch[start] !== " ") start--;
  }
  if (end < messageToSearch.length && messageToSearch[end] !== " ") {
    while (end < messageToSearch.length && messageToSearch[end] !== " ") end++;
  }
  // If that word is longer than the context length, then cut it off
  if (start + contextLength < end) {
    end = start + contextLength;
  }

  const truncatedMessage = messageToSearch.slice(start, end).trim();
  // Escape: the keyword is raw user input, so metacharacters either mis-highlight ("3.14"
  // matching "3x14") or throw from the RegExp constructor (a lone "["), killing the results.
  const parts = truncatedMessage.split(new RegExp(`(${escapeRegExp(keyword)})`, "gi"));

  return [start, end, parts];
};
